import { DiscordSDK } from "@discord/embedded-app-sdk";

export interface DiscordSession {
  /** When true, we're running outside of Discord (local dev tab). */
  mock: boolean;
  user: {
    id: string;
    username: string;
    globalName: string;
    avatar: string | null;
  };
  /** Channel/instance scoping the room; we hash this into the room code. */
  instanceId: string;
  /** Cosmetics from Discord IAP entitlements. */
  cosmetics: { catnipPack: boolean };
  /** Triggers a Discord-native purchase flow for the given SKU id. */
  purchase: (skuId: string) => Promise<boolean>;
}

const CLIENT_ID = (import.meta.env.VITE_DISCORD_CLIENT_ID ?? "").trim();

// Configure these when you create SKUs in the Discord developer portal.
// https://discord.com/developers/docs/monetization/managing-skus
export const SKUS = {
  catnipPack: import.meta.env.VITE_SKU_CATNIP_PACK ?? "",
} as const;

export async function initDiscord(): Promise<DiscordSession> {
  // Discord injects `frame_id` into the URL when launching an activity.
  const isInDiscord =
    new URLSearchParams(location.search).has("frame_id") && !!CLIENT_ID;

  if (!isInDiscord) {
    return mockSession();
  }

  const sdk = new DiscordSDK(CLIENT_ID);
  await sdk.ready();

  // 1) authorize -> code, 2) exchange code for token, 3) authenticate the SDK.
  const { code } = await sdk.commands.authorize({
    client_id: CLIENT_ID,
    response_type: "code",
    state: "",
    prompt: "none",
    scope: ["identify", "guilds.members.read"],
  });

  const tokenRes = await fetch("/.proxy/api/token", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!tokenRes.ok) throw new Error("token exchange failed");
  const { access_token } = (await tokenRes.json()) as { access_token: string };

  const auth = await sdk.commands.authenticate({ access_token });

  const cosmetics = await loadEntitlements(sdk);

  return {
    mock: false,
    user: {
      id: auth.user.id,
      username: auth.user.username,
      globalName: (auth.user as { global_name?: string }).global_name ?? auth.user.username,
      avatar: auth.user.avatar
        ? `https://cdn.discordapp.com/avatars/${auth.user.id}/${auth.user.avatar}.png?size=128`
        : null,
    },
    instanceId: sdk.instanceId,
    cosmetics,
    purchase: async (skuId: string) => {
      if (!skuId) return false;
      try {
        await sdk.commands.startPurchase({ sku_id: skuId });
        const fresh = await loadEntitlements(sdk);
        cosmetics.catnipPack = fresh.catnipPack;
        return fresh.catnipPack;
      } catch (err) {
        console.warn("purchase failed", err);
        return false;
      }
    },
  };
}

async function loadEntitlements(
  sdk: DiscordSDK
): Promise<{ catnipPack: boolean }> {
  try {
    const result = await sdk.commands.getEntitlements();
    const ents = (result as { entitlements?: Array<{ sku_id: string }> }).entitlements ?? [];
    return {
      catnipPack: ents.some((e) => e.sku_id === SKUS.catnipPack),
    };
  } catch {
    return { catnipPack: false };
  }
}

function mockSession(): DiscordSession {
  // Stable per-tab identity so the server treats refreshes as reconnects.
  const id =
    sessionStorage.getItem("mockUserId") ??
    (() => {
      const v = "dev_" + Math.random().toString(36).slice(2, 10);
      sessionStorage.setItem("mockUserId", v);
      return v;
    })();
  const name = sessionStorage.getItem("mockUserName") ?? "DevCat-" + id.slice(-3);
  return {
    mock: true,
    user: { id, username: name, globalName: name, avatar: null },
    instanceId: "DEV",
    cosmetics: { catnipPack: false },
    purchase: async () => {
      alert("Purchases require running inside Discord.");
      return false;
    },
  };
}
